import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, RotateCcw, Clock, Search, AlertTriangle } from "lucide-react";
import { useBulkSelection, BulkActionBar, BulkSelectAllCheckbox } from "@/components/BulkActions";
import { Checkbox } from "@/components/ui/checkbox";

const collectionLabels = {
  clients: "Client",
  livraisons: "Livraison",
  tournees: "Tournée",
  ventes: "Vente",
  produits: "Produit",
  charges: "Charge",
  pertes: "Perte",
  devis: "Devis",
  impayes: "Impayé",
  taches: "Tâche",
};

const Corbeille = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [collectionFilter, setCollectionFilter] = useState("all");

  const fetchTrash = async () => {
    try {
      const r = await axios.get(`${API}/trash`, { headers });
      setItems(r.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTrash(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (collectionFilter !== "all" && i.collection !== collectionFilter) return false;
      if (search.trim() && !(i.label || "").toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, search, collectionFilter]);

  const { selectedIds, toggleOne, toggleAll, isSelected, allChecked, someChecked, clear, count } = useBulkSelection(filtered);

  const restore = async (id) => {
    try {
      await axios.post(`${API}/trash/${id}/restore`, {}, { headers });
      toast.success("Restauré");
      fetchTrash();
    } catch { toast.error("Erreur"); }
  };

  const deletePermanent = async (id) => {
    if (!window.confirm("Supprimer définitivement ? Cette action est irréversible.")) return;
    try {
      await axios.delete(`${API}/trash/${id}`, { headers });
      toast.success("Supprimé définitivement");
      fetchTrash();
    } catch { toast.error("Erreur"); }
  };

  const bulkRestore = async () => {
    for (const id of selectedIds) {
      try { await axios.post(`${API}/trash/${id}/restore`, {}, { headers }); } catch { /* skip */ }
    }
    toast.success(`${selectedIds.length} élément(s) restauré(s)`);
    clear();
    fetchTrash();
  };

  const bulkDeletePermanent = async () => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${selectedIds.length} élément(s) ?\nCette action est IRRÉVERSIBLE.`)) return;
    for (const id of selectedIds) {
      try { await axios.delete(`${API}/trash/${id}`, { headers }); } catch { /* skip */ }
    }
    toast.success("Supprimé définitivement");
    clear();
    fetchTrash();
  };

  const emptyAll = async () => {
    if (!window.confirm("Vider ENTIÈREMENT la corbeille ? Tous les éléments seront définitivement supprimés.")) return;
    try {
      const r = await axios.post(`${API}/trash/empty`, {}, { headers });
      toast.success(`${r.data.count} élément(s) supprimés`);
      fetchTrash();
    } catch { toast.error("Erreur"); }
  };

  const daysLeft = (expires_at) => {
    try {
      const diff = new Date(expires_at).getTime() - Date.now();
      return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    } catch { return 0; }
  };

  const collections = useMemo(() => {
    const set = new Set(items.map((i) => i.collection));
    return Array.from(set);
  }, [items]);

  if (loading) return <div className="text-center py-12 text-slate-400">Chargement…</div>;

  return (
    <div className="space-y-4" data-testid="corbeille-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Corbeille</h1>
          <p className="text-slate-500 mt-1">Les éléments supprimés sont conservés 15 jours avant suppression définitive</p>
        </div>
        {items.length > 0 && (
          <Button variant="outline" onClick={emptyAll} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="empty-trash-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Vider la corbeille
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="corbeille-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setCollectionFilter("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium ${collectionFilter === "all" ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="corbeille-filter-all">
            Tous ({items.length})
          </button>
          {collections.map((c) => (
            <button key={c} onClick={() => setCollectionFilter(c)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${collectionFilter === c ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid={`corbeille-filter-${c}`}>
              {collectionLabels[c] || c} ({items.filter((x) => x.collection === c).length})
            </button>
          ))}
        </div>
      </div>

      {count > 0 && (
        <BulkActionBar
          count={count}
          onClear={clear}
          customActions={[
            { label: "Restaurer", icon: RotateCcw, onClick: bulkRestore, variant: "outline", className: "text-emerald-600 border-emerald-200 hover:bg-emerald-50", testid: "bulk-restore-btn" },
            { label: "Supprimer définitivement", icon: Trash2, onClick: bulkDeletePermanent, variant: "outline", className: "text-red-600 border-red-200 hover:bg-red-50", testid: "bulk-delete-permanent-btn" },
          ]}
          itemLabel="élément"
        />
      )}

      <Card>
        <CardHeader className="py-3 border-b">
          <div className="flex items-center gap-3">
            <BulkSelectAllCheckbox allChecked={allChecked} someChecked={someChecked} onToggle={toggleAll} />
            <CardTitle className="text-sm font-medium text-slate-600">{filtered.length} élément(s)</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-slate-400">
              <Trash2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">La corbeille est vide</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((t) => {
                const days = daysLeft(t.expires_at);
                return (
                  <div key={t.id} className="p-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`trash-row-${t.id}`}>
                    <Checkbox checked={isSelected(t.id)} onCheckedChange={() => toggleOne(t.id)} data-testid={`trash-check-${t.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 text-sm truncate">{t.label}</p>
                      <p className="text-xs text-slate-400">
                        <span className="inline-block px-1.5 py-0.5 bg-slate-100 rounded mr-2">{collectionLabels[t.collection] || t.collection}</span>
                        Supprimé le {new Date(t.deleted_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-400 mr-2">
                      <Clock className="w-3 h-3" />
                      <span className={days <= 3 ? "text-red-600 font-medium" : ""}>
                        {days} j restants
                      </span>
                      {days <= 3 && <AlertTriangle className="w-3 h-3 text-red-500" />}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => restore(t.id)} className="text-emerald-600 hover:bg-emerald-50" data-testid={`restore-${t.id}`}>
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deletePermanent(t.id)} className="text-red-600 hover:bg-red-50" data-testid={`delete-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Corbeille;
